// `/admin` — everything Scout has done, on one surface, for one person.
//
// Scout writes to production unattended. On Wednesday 9 Sep it created 36 Salesforce Accounts,
// adopted 30 and attached 9 in 45 minutes, and the evidence was spread across five Postgres
// tables, Render's cron logs and Salesforce field values, readable only by whoever ran `psql`.
// #175 then removed Salesforce Tasks on the argument that `intake_events` is richer and the
// dashboard would read it. This is that promise.
//
// The audience is one person. The order is the order the questions get asked: what is wrong
// and what is waiting (the strip, which links to `/available#proposals`), then did the jobs run,
// then what did Scout do, the briefs, the roles, the Q4 targets upload, and Scout itself.
//
// Every route behind this page is gated by `require_admin_view` — a grant, not a `sees_*`
// flag, so `leadership` is refused — and refused outright under a view-as. The redirect below
// is convenience, never the boundary.

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccessDeniedError, fetchAdminBriefs, fetchAdminOverview, fetchAdminRoles,
  fetchBook, fetchIntakeEvents, fetchJobRuns, fetchMe,
} from '../api/client';
import type { AdminRolesPayload } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { DeniedGate } from '../auth/Gate';
import { AdminSection } from '../admin/AdminSection';
import { BriefsPanel } from '../admin/BriefsPanel';
import { IntakeEventsTable, EMPTY_KNOCK_FILTERS } from '../admin/IntakeEventsTable';
import type { KnockFilters } from '../admin/IntakeEventsTable';
import { JobRunsTable } from '../admin/JobRunsTable';
import { Q4UploadBox } from '../admin/Q4UploadBox';
import { RolesEditor } from '../admin/RolesEditor';
import { StatusStrip } from '../admin/StatusStrip';
import { PageChrome } from '../components/PageChrome';
import { ChatPanel } from '../sidebar/ChatEmbed';
import { formatCount } from '../lib/format';

export function AdminView() {
  const { getToken, email, signOut } = useAuthContext();
  const queryClient = useQueryClient();

  const [knockFilters, setKnockFilters] = useState<KnockFilters>(EMPTY_KNOCK_FILTERS);
  const [jobName, setJobName] = useState('');
  const [briefOn, setBriefOn] = useState('');

  // `/api/me` rather than the book: the tab and the redirect need the viewer block and
  // nothing else, and the book is 694KB somebody may be about to navigate away from.
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => fetchMe(getToken) });
  const viewer = meQuery.data?.viewer;
  const allowed = Boolean(viewer?.sees_admin_view);

  const overviewQuery = useQuery({
    queryKey: ['admin-overview'],
    queryFn: () => fetchAdminOverview(getToken),
    enabled: allowed,
  });
  const runsQuery = useQuery({
    queryKey: ['admin-job-runs', jobName],
    queryFn: () => fetchJobRuns(getToken, jobName || undefined),
    enabled: allowed,
  });
  const knocksQuery = useQuery({
    queryKey: ['admin-intake-events', knockFilters],
    queryFn: () => fetchIntakeEvents(getToken, {
      outcome: knockFilters.outcome || undefined,
      source: knockFilters.source || undefined,
      reason: knockFilters.reason || undefined,
      domain: knockFilters.domain.trim() || undefined,
    }),
    enabled: allowed,
  });
  const briefsQuery = useQuery({
    queryKey: ['admin-briefs', briefOn],
    queryFn: () => fetchAdminBriefs(getToken, briefOn || undefined),
    enabled: allowed,
  });
  const rolesQuery = useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => fetchAdminRoles(getToken),
    enabled: allowed,
  });

  const bookQuery = useQuery({
    queryKey: ['book'],
    queryFn: () => fetchBook(getToken),
    enabled: allowed,
  });

  if (meQuery.error instanceof AccessDeniedError) {
    return <DeniedGate message={meQuery.error.message} email={email} onSignOut={signOut} />;
  }
  if (meQuery.isPending) {
    return <div className="loading">Signing you in…</div>;
  }

  // Hide, do not stub. A bookmark to /admin from a strategist lands on the board they have.
  if (viewer && !viewer.sees_admin_view) {
    return <Navigate to={viewer.default_view === 'execution' ? '/execution' : '/activity'} replace />;
  }

  const overview = overviewQuery.data;
  const knocks = knocksQuery.data;
  const runs = runsQuery.data;
  const briefs = briefsQuery.data;
  const roles = rolesQuery.data;

  function applyRoles(next: AdminRolesPayload) {
    queryClient.setQueryData<AdminRolesPayload>(['admin-roles'], next);
  }

  return (
    <PageChrome
      freshness={bookQuery.data?.freshness ?? null}
      build={bookQuery.data?.build}
      viewer={viewer}
    >
      <p className="sub">
        Everything Scout has done, from Postgres rather than from Salesforce. Reads are live;
        the book strip above is as fresh as the twice-daily mirror behind it.
      </p>

      {overviewQuery.error ? (
        <div className="err">
          <b>Could not load the summary.</b>
          <br />
          {(overviewQuery.error as Error).message}
        </div>
      ) : null}
      {overviewQuery.isPending ? <p className="adminloading">Reading Postgres…</p> : null}
      {overview?.unavailable ? (
        <p className="adminunavailable">{overview.unavailable}</p>
      ) : (
        <StatusStrip overview={overview} />
      )}

      <AdminSection
        title="Did the jobs run?"
        count={runs ? `${formatCount(runs.runs.length)} shown` : null}
        loading={runsQuery.isPending}
        unavailable={runs?.unavailable}
      >
        <JobRunsTable payload={runs} jobName={jobName} onJobName={setJobName} />
      </AdminSection>

      <AdminSection
        title="What did Scout do?"
        count={knocks ? `${formatCount(knocks.events.length)} knocks` : null}
        loading={knocksQuery.isPending}
        unavailable={knocks?.unavailable}
      >
        <IntakeEventsTable
          payload={knocks}
          filters={knockFilters}
          onFilters={setKnockFilters}
        />
      </AdminSection>

      <AdminSection
        title="What did people get told?"
        count={briefs ? `${formatCount(briefs.briefs.length)} briefs` : null}
        asOf={briefs?.brief_on ? `for ${briefs.brief_on}` : null}
        loading={briefsQuery.isPending}
        unavailable={briefs?.unavailable}
      >
        <BriefsPanel payload={briefs} briefOn={briefOn} onBriefOn={setBriefOn} />
      </AdminSection>

      <AdminSection
        title="Who can use this thing?"
        count={roles ? `${formatCount(roles.people.length)} people` : null}
        loading={rolesQuery.isPending}
        unavailable={roles?.unavailable}
      >
        <RolesEditor payload={roles} onChanged={applyRoles} />
      </AdminSection>

      {/* Admin-only like the rest of the page; mounted only once the viewer is known to be allowed,
          because the box reads its staged upload on mount. */}
      {allowed ? (
        <AdminSection title="Q4 targets CSV">
          <Q4UploadBox />
        </AdminSection>
      ) : null}

      <AdminSection title="Ask Scout">
        <div className="adminchat">
          <ChatPanel />
        </div>
      </AdminSection>
    </PageChrome>
  );
}
